import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./Pages.css";

const About = () => {
  return (
    <main>
      <Helmet>
        <title>About Us | Seagull Partners</title>
        <meta
          name="description"
          content="Learn about Seagull Partners—our mission, values, and approach to helping B2B teams grow with data-driven strategy, marketing, and technology at every stage."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="page-hero">
        
        <div className="container">
          <h1 className="page-hero-title">
            Seagull — Your Growth, Our Strategy
          </h1>
          <p className="page-hero-subtitle">
            At Seagull, we help businesses scale smarter with data-driven
            solutions, innovative technology, and strategic insights tailored to
            your goals.
          </p>
          <Link to="/contact" className="btn">
            📩 Get in Touch
          </Link>
        </div>
      </section>

      {/* Our Story */}
      <section className="section">
        <div className="container">
          <div className="content-section">
            <h2 className="section-title">Our Journey</h2>
            <div className="content-text">
              <p>
                Seagull was founded with one clear mission: to help businesses
                grow smarter, faster, and more efficiently. We realized that too
                many companies were drowning in data without a clear strategy,
                and others relied on generic solutions that didn't deliver real
                results.
              </p>
              <p>
                Our team brings together expertise in{" "}
                <strong>
                  Data Intelligence, Digital Marketing, AI Tools, and Business
                  Strategy
                </strong>
                , creating solutions that are{" "}
                <strong>customized, actionable, and results-focused</strong>.
              </p>
              <p>
                From our first client to our growing portfolio today, our goal
                has remained the same:{" "}
                <strong>
                  turn insights into action, and action into growth.
                </strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Values */}
      <section className="section vision-values-section">
        <div className="container">
          <div className="content-section">
            <h2 className="section-title">What Drives Us</h2>

            <div className="vision-section">
              <h3 className="subsection-title">Vision</h3>
              <p className="vision-text">
                To be the most trusted growth partner for businesses worldwide,
                helping them achieve measurable success through smart,
                data-driven solutions.
              </p>
            </div>

            <div className="values-section">
              <h3 className="subsection-title">Values</h3>
              <div className="values-grid">
                <div className="value-card">
                  <h4>Innovation</h4>
                  <p>
                    We continuously explore smarter ways to solve business
                    challenges.
                  </p>
                </div>
                <div className="value-card">
                  <h4>Integrity</h4>
                  <p>Transparent and honest solutions you can rely on.</p>
                </div>
                <div className="value-card">
                  <h4>Results</h4>
                  <p>
                    Measurable outcomes are at the core of everything we do.
                  </p>
                </div>
                <div className="value-card">
                  <h4>Collaboration</h4>
                  <p>Your success is our success — we grow together.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="section">
        <div className="container">
          <div className="content-section">
            <h2 className="section-title">Our Approach</h2>
            <div className="content-text">
              <p>
                At Seagull, we don't offer one-size-fits-all solutions. Every
                business is unique, so every strategy we create is tailored to
                your goals.
              </p>
              <p>Our approach is simple:</p>

              <div className="approach-steps">
                <div className="approach-step">
                  <h3>1. Understand</h3>
                  <p>Dive deep into your business, market, and challenges.</p>
                </div>
                <div className="approach-step">
                  <h3>2. Design</h3>
                  <p>
                    Build a custom strategy combining data, technology, and
                    expert insight.
                  </p>
                </div>
                <div className="approach-step">
                  <h3>3. Execute</h3>
                  <p>
                    Implement solutions efficiently and optimize continuously
                    for maximum impact.
                  </p>
                </div>
              </div>

              <p className="approach-conclusion">
                This approach ensures that our clients don't just get services —
                they get <strong>growth that matters</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Partners Section */}
      <section className="section partners-section">
        <div className="container">
          <div className="content-section">
            <h2 className="section-title">Our Trusted Partners</h2>
            <p className="section-intro">
              We collaborate with industry leaders to deliver exceptional solutions
            </p>

            <div className="partners-grid">
              {/* Oracle */}
              <div className="partner-card" data-aos="fade-up">
                <div className="partner-logo-wrapper">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/5/50/Oracle_logo.svg"
                    alt="Oracle"
                    className="partner-logo"
                  />
                </div>
                <div className="partner-info">
                  <h4 className="partner-name">Oracle</h4>
                  <p className="partner-desc">Cloud Infrastructure & Database</p>
                </div>
              </div>

              {/* SAP */}
              <div className="partner-card" data-aos="fade-up" data-aos-delay="100">
                <div className="partner-logo-wrapper">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/5/59/SAP_2011_logo.svg"
                    alt="SAP"
                    className="partner-logo"
                  />
                </div>
                <div className="partner-info">
                  <h4 className="partner-name">SAP</h4>
                  <p className="partner-desc">Enterprise Resource Planning</p>
                </div>
              </div>

              {/* Dun & Bradstreet */}
              <div className="partner-card" data-aos="fade-up" data-aos-delay="200">
                <div className="partner-logo-wrapper">
                  <img
                    src="/Bradstreet_wordmark.svg.png"
                    alt="Dun & Bradstreet"
                    className="partner-logo dun-bradstreet"
                  />
                </div>
                <div className="partner-info">
                  <h4 className="partner-name">Dun & Bradstreet</h4>
                  <p className="partner-desc">Business Data & Analytics</p>
                </div>
              </div>

              {/* Marketo */}
              <div className="partner-card" data-aos="fade-up" data-aos-delay="300">
                <div className="partner-logo-wrapper">
                  <img
                    src="/Marketo_logo.svg.png"
                    alt="Marketo"
                    className="partner-logo marketo"
                  />
                </div>
                <div className="partner-info">
                  <h4 className="partner-name">Marketo</h4>
                  <p className="partner-desc">Marketing Automation Platform</p>
                </div>
              </div>
            </div>

            <p className="partners-conclusion">
              Through strategic partnerships, we bring{" "}
              <strong>world-class technology, expertise, and innovation</strong>{" "}
              to every project, ensuring our clients receive the best possible solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Why Seagull */}
      <section className="section why-section">
        <div className="container">
          <div className="content-section">
            <h2 className="section-title">Why Partner with Us?</h2>

            <div className="reasons-list">
              <div className="reason-item">
                ✔ Strategy-first approach, not cookie-cutter solutions
              </div>
              <div className="reason-item">
                ✔ Custom solutions designed around your goals
              </div>
              <div className="reason-item">
                ✔ Focused on <strong>quality, accuracy, and results</strong>
              </div>
              <div className="reason-item">
                ✔ Long-term growth partner, not just a service provider
              </div>
              <div className="reason-item">
                ✔ Dedicated expert guidance every step of the way
              </div>
            </div>

            <div className="cta-container">
              <Link to="/contact" className="btn">
                📩 Let's Talk About Your Growth
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA
      <section className="section footer-cta-section">
        <div className="container">
          <div className="content-section text-center">
            <h2 className="section-title">
              Ready to Think Smarter and Rise Faster?
            </h2>
            <p className="section-intro">
              Reach out today and see how Seagull can help your business grow with confidence.
            </p>
            <Link to="/contact" className="btn">
              📩 Contact Us
            </Link>
          </div>
        </div>
      </section> */}
    </main>
  );
};

export default About;
